import { EntityTypes } from 'constants/entities';
import { ICreate } from 'core/actions/admin/data';
import { cloneDeep } from 'lodash';
import { IVerificationResponseDAO } from 'types/domain/verification';
import { IActionAudioDAO, IActionDAO, IActionEntityDAO } from 'types/domain/action';
import { IAnimationDAO } from 'types/domain/animation';
import { IAudioDAO } from 'types/domain/audio';
import { IPortraitVariantDAO, LoadingState } from 'types/domain/base';
import { ICertificateDAO } from 'types/domain/certificate';
import {
  ICourseDAO,
  ICourseNodeDAO,
  ICourseNodeEntityDAO,
  ICourseNodeGroupDAO,
  ICourseOutDAO,
  ICourseUserVariable,
  ICourseVariableDAO,
} from 'types/domain/course';
import { IFolderDAO } from 'types/domain/folder';
import { IForumPostDAO, IForumTopicDAO } from 'types/domain/forum';
import { IGroupDAO, IGroupUserDAO } from 'types/domain/group';
import { IImageDAO } from 'types/domain/image';
import { IModelDAO } from 'types/domain/model';
import { IModuleAssign, IModuleDAO, ModuleStage } from 'types/domain/module';
import { INotificationDAO } from 'types/domain/notifications';
import { IDomain, IOrganisationConfigDAO } from 'types/domain/organisation';
import {
  ICoursePermission,
  ICourseProgressDAO,
  IGroupAccessPermissionDAO,
  ISystemGroupDAO,
} from 'types/domain/permissions';

import { ISceneConfigDAO, ISceneDAO } from 'types/domain/scene';
import { IStructureDAO, IStructureGroupInstanceDAO } from 'types/domain/structure';
import { ITextDAO, ITextSearchDAO } from 'types/domain/text';
import { ITranscriptionDAO } from 'types/domain/transcription';
import {
  IOrganisationUserDAO,
  IOrgUserCertificateDAO,
  IOrgUserCertificateReport,
  IOrgUserCourse,
  IOrgUserVariableDAO,
} from 'types/domain/user';
import { IVariableDAO } from 'types/domain/variable';
import { IVerificationDAO, IVerificationRequestDAO, IVerificationRequestEntity } from 'types/domain/verification';
import { IVideoDAO } from 'types/domain/video';
import { get } from '../cases';
import { ISessionAttempt } from 'types/domain/session';
import { IOrganisationSystemDAO, IOrganisationSystemValue, ISystemAttribute, ISystemDAO } from 'types/domain/system';
import { beTarask } from 'date-fns/locale';

export default function merge(state, entityType: EntityTypes, id, data) {
  if (!get(state, entityType) || !data) return state;

  if (!id)
    if (data && data instanceof Array)
      return data.forEach((ent: any) => {
        if (ent) merge(state, entityType, ent.id, ent);
      });
    else return console.warn('could not merge' + entityType, data);

  if (data.offlineId) offlineEntity(state, entityType, data);

  switch (entityType) {
    case EntityTypes.ACTION:
      const action = data as IActionDAO;

      if (action.entities) mergeAndDelete(state, EntityTypes.ACTION_ENTITY, null, action, 'entities');
      if (action.scene) mergeAndDelete(state, EntityTypes.SCENE, action.scene.id, action, 'scene');

      if (action.sceneConfig)
        mergeAndDelete(state, EntityTypes.SCENE_CONFIG, action.sceneConfig.id, action, 'sceneConfig');

      if (action.voiceScene) mergeAndDelete(state, EntityTypes.SCENE, action.voiceScene.id, action, 'voiceScene');

      if (action.voiceSceneConfig)
        mergeAndDelete(state, EntityTypes.SCENE_CONFIG, action.voiceSceneConfig.id, action, 'voiceSceneConfig');

      if (action.modOut) mergeAndDelete(state, EntityTypes.MODULE_OUT, action.modOut.id, action, 'modOut');

      genericMerge(state, action);
      mergeState(state, entityType, action);
      return;
    case EntityTypes.ACTION_AUDIO:
      const actionAudio = data as IActionAudioDAO;

      if (actionAudio.audio) mergeAndDelete(state, EntityTypes.AUDIO, actionAudio.audio.id, actionAudio, 'audio');

      mergeState(state, entityType, actionAudio);
      return;
    case EntityTypes.ACTION_ENTITY:
      const actionEntity = data as IActionEntityDAO;

      if (actionEntity.audio) mergeAndDelete(state, EntityTypes.AUDIO, actionEntity.audio.id, actionEntity, 'audio');
      if (actionEntity.video) mergeAndDelete(state, EntityTypes.VIDEO, actionEntity.video.id, actionEntity, 'video');
      if (actionEntity.model) mergeAndDelete(state, EntityTypes.MODEL, actionEntity.model.id, actionEntity, 'model');
      if (actionEntity.referencePosition)
        mergeAndDelete(
          state,
          EntityTypes.POSITION,
          actionEntity.referencePosition.id,
          actionEntity,
          'referencePosition',
        );
      if (actionEntity.pPosition)
        mergeAndDelete(state, EntityTypes.POSITION, actionEntity.pPosition.id, actionEntity, 'pPosition');

      genericMerge(state, actionEntity);
      mergeState(state, entityType, actionEntity);
      return;
    case EntityTypes.ANIMATION:
      const animation = data as IAnimationDAO;

      if (animation.position) mergeAndDelete(state, EntityTypes.POSITION, animation.position.id, animation, 'position');
      if (animation.rotation) mergeAndDelete(state, EntityTypes.ROTATION, animation.rotation.id, animation, 'rotation');

      mergeState(state, entityType, animation);
      return;
    case EntityTypes.AUDIO:
      const audio = data as IAudioDAO;

      if (audio.files) mergeAndDelete(state, EntityTypes.AUDIO_FILE, null, audio.files, 'files');

      mergeState(state, entityType, audio);
      return;
    case EntityTypes.CERTIFICATE:
      const certificate = data as ICertificateDAO;

      if (certificate.image) mergeAndDelete(state, EntityTypes.IMAGE, certificate.image.id, certificate, 'image');
      if (certificate.text) mergeAndDelete(state, EntityTypes.TEXT, certificate.text.id, certificate, 'text');

      if (certificate.organisationUserCertificates)
        mergeAndDelete(state, EntityTypes.ORG_USER_CERTIFICATE, null, certificate, 'organisationUserCertificates');

      mergeState(state, entityType, certificate);
      return;
    case EntityTypes.COURSE_VARIABLE:
      const courseVariable = data as ICourseVariableDAO;

      if (courseVariable.variable)
        mergeAndDelete(state, EntityTypes.VARIABLE, courseVariable.variable.id, courseVariable, 'variable');

      mergeState(state, entityType, courseVariable);
      return;
    case EntityTypes.COURSE_USER_VARIABLE:
      const courseUserVariable = data as ICourseUserVariable;

      if (courseUserVariable.courseVariable)
        mergeAndDelete(
          state,
          EntityTypes.COURSE_VARIABLE,
          courseUserVariable.courseVariable,
          courseUserVariable,
          'courseVariable',
        );
      if (courseUserVariable.organisationUserVariable)
        mergeAndDelete(
          state,
          EntityTypes.ORG_USER_VARIABLE,
          courseUserVariable.organisationUserVariable,
          courseUserVariable,
          'organisationUserVariable',
        );

      mergeState(state, entityType, courseUserVariable);
      return;
    case EntityTypes.COURSE:
      const course = data as ICourseDAO;

      const existing = state[EntityTypes.COURSE][course.id] as ICourseDAO;
      if (existing && course.courseProgressCount) {
        course.courseProgressCountChange = course.courseProgressCount - (existing.courseProgressCount ?? 0);
        course.courseProgressCompleteCountChange =
          course.courseProgressCompleteCount - (existing.courseProgressCompleteCount ?? 0);
      }

      if (course.text) mergeAndDelete(state, EntityTypes.TEXT, course.text.id, course, 'text');
      if (course.outs) mergeAndDelete(state, EntityTypes.COURSE_OUT, null, course, 'outs');
      if (course.nodes) mergeAndDelete(state, EntityTypes.COURSE_NODE, null, course, 'nodes');
      if (course.courseVariables) mergeAndDelete(state, EntityTypes.COURSE_VARIABLE, null, course, 'courseVariables');
      if (course.coursePermission)
        mergeAndDelete(state, EntityTypes.COURSE_PERMISSION, course.coursePermission.id, course, 'coursePermission');
      if (course.latestCourseProgress)
        mergeAndDelete(state, EntityTypes.COURSE_PROGRESS, null, course, 'latestCourseProgress');
      genericMerge(state, course);
      mergeState(state, entityType, course);
      return;
    case EntityTypes.COURSE_NODE:
      const courseNode = data as ICourseNodeDAO;

      if (courseNode.certificate)
        mergeAndDelete(state, EntityTypes.CERTIFICATE, courseNode.certificate.id, courseNode, 'certificate');
      if (courseNode.group) mergeAndDelete(state, EntityTypes.GROUP, courseNode.group.id, courseNode, 'group');
      if (courseNode.module) mergeAndDelete(state, EntityTypes.MODULE, courseNode.module.id, courseNode, 'module');
      if (courseNode.service) mergeAndDelete(state, EntityTypes.SERVICE, courseNode.service.id, courseNode, 'service');
      if (courseNode.system) mergeAndDelete(state, EntityTypes.SYSTEM, courseNode.system.id, courseNode, 'system');

      if (courseNode.organisationSystem)
        mergeAndDelete(
          state,
          EntityTypes.ORG_SYSTEM,
          courseNode.organisationSystem.id,
          courseNode,
          'organisationSystem',
        );
      if (courseNode.variable)
        mergeAndDelete(state, EntityTypes.VARIABLE, courseNode.variable.id, courseNode, 'variable');
      if (courseNode.courseVariable)
        mergeAndDelete(state, EntityTypes.COURSE_VARIABLE, courseNode.courseVariable.id, courseNode, 'courseVariable');
      if (courseNode.courseVariables)
        mergeAndDelete(state, EntityTypes.COURSE_VARIABLE, null, courseNode, 'courseVariables');

      if (courseNode.courseOuts) mergeAndDelete(state, EntityTypes.COURSE_OUT, null, courseNode, 'courseOuts');

      if (courseNode.entities) mergeAndDelete(state, EntityTypes.COURSE_NODE_ENTITY, null, courseNode, 'entities');

      if (courseNode.courseNodeGroups)
        mergeAndDelete(state, EntityTypes.COURSE_NODE_GROUP, null, courseNode, 'courseNodeGroups');

      if (courseNode.courseProgressCurrent)
        mergeAndDelete(state, EntityTypes.COURSE_PROGRESS, null, courseNode, 'courseProgressCurrent');

      genericMerge(state, courseNode);
      mergeState(state, entityType, courseNode);
      return;
    case EntityTypes.COURSE_OUT:
      const courseOut = data as ICourseOutDAO;

      if (courseOut.target) mergeAndDelete(state, EntityTypes.COURSE_NODE, courseOut.target.id, courseOut, 'target');

      mergeState(state, entityType, courseOut);
      return;
    case EntityTypes.COURSE_NODE_GROUP:
      const courseNodeGroup = data as ICourseNodeGroupDAO;
      if (courseNodeGroup.group)
        mergeAndDelete(state, EntityTypes.GROUP, courseNodeGroup.group.id, courseNodeGroup, 'group');
      mergeState(state, entityType, courseNodeGroup);
      break;
    case EntityTypes.COURSE_NODE_ENTITY:
      const courseNodeEntity = data as ICourseNodeEntityDAO;
      if (courseNodeEntity.group)
        mergeAndDelete(state, EntityTypes.GROUP, courseNodeEntity.group.id, courseNodeEntity, 'group');

      if (courseNodeEntity.variable)
        mergeAndDelete(state, EntityTypes.VARIABLE, courseNodeEntity.variable.id, courseNodeEntity, 'variable');
      genericMerge(state, courseNodeEntity);
      mergeState(state, entityType, courseNodeEntity);
      return;
    case EntityTypes.COURSE_PERMISSION:
      const coursePermission = data as ICoursePermission;

      if (coursePermission.organisationUser)
        mergeAndDelete(
          state,
          EntityTypes.ORG_USER,
          coursePermission.organisationUser.id,
          coursePermission,
          'organisationUser',
        );

      mergeState(state, entityType, coursePermission);
      return;
    case EntityTypes.COURSE_PROGRESS:
      const courseProgress = data as ICourseProgressDAO;

      if (courseProgress.module)
        mergeAndDelete(state, EntityTypes.MODULE, courseProgress.module.id, courseProgress, 'module');

      if (courseProgress.sessionAttempt)
        mergeAndDelete(
          state,
          EntityTypes.SESSION_ATTEMPT,
          courseProgress.sessionAttempt.id,
          courseProgress,
          'sessionAttempt',
        );

      if (courseProgress.sessionAttempts)
        mergeAndDelete(state, EntityTypes.SESSION_ATTEMPT, null, courseProgress, 'sessionAttempts');

      if (courseProgress.organisationUser)
        mergeAndDelete(
          state,
          EntityTypes.ORG_USER,
          courseProgress.organisationUser.id,
          courseProgress,
          'organisationUser',
        );

      if (courseProgress.organisationUserVariables)
        mergeAndDelete(state, EntityTypes.ORG_USER_VARIABLE, null, courseProgress, 'organisationUserVariables');

      if (courseProgress.organisationUserCertificates)
        mergeAndDelete(state, EntityTypes.ORG_USER_CERTIFICATE, null, courseProgress, 'organisationUserCertificates');

      if (courseProgress.emails) mergeAndDelete(state, EntityTypes.EMAIL, null, courseProgress, 'emails');

      if (courseProgress.lastEmail)
        mergeAndDelete(state, EntityTypes.EMAIL, courseProgress.lastEmail.id, courseProgress, 'lastEmail');

      if (courseProgress.lastOrganisationUserVariable)
        mergeAndDelete(
          state,
          EntityTypes.ORG_USER_VARIABLE,
          courseProgress.lastOrganisationUserVariable.id,
          courseProgress,
          'lastOrganisationUserVariable',
        );

      if (courseProgress.lastGroupUser)
        mergeAndDelete(state, EntityTypes.GROUP_USER, courseProgress.lastGroupUser.id, courseProgress, 'lastGroupUser');

      if (courseProgress.organisationUser)
        mergeAndDelete(
          state,
          EntityTypes.ORG_USER,
          courseProgress.organisationUser.id,
          courseProgress,
          'organisationUser',
        );

      genericMerge(state, courseProgress);
      mergeState(state, entityType, courseProgress);
      return;
    case EntityTypes.DOMAIN:
      const domain = data as IDomain;
      mergeState(state, entityType, domain);
      return;
    case EntityTypes.FOLDER:
      const folder = data as IFolderDAO;
      if (folder.scenes) mergeAndDelete(state, EntityTypes.SCENE, null, folder, 'scenes');
      if (folder.modules) mergeAndDelete(state, EntityTypes.MODULE, null, folder, 'modules');

      mergeState(state, entityType, folder);
      return;
    case EntityTypes.FORUM_POST:
      const forumPost = data as IForumPostDAO;

      if (forumPost.text) mergeAndDelete(state, EntityTypes.TEXT, null, forumPost.text.id, 'text');
      if (forumPost.mentions) mergeAndDelete(state, EntityTypes.FORUM_POST_MENTION, null, forumPost, 'mentions');
      if (forumPost.views) mergeAndDelete(state, EntityTypes.FORUM_POST_VIEW, null, forumPost, 'views');
      genericMerge(state, forumPost);
      mergeState(state, entityType, forumPost);
      return;
    case EntityTypes.FORUM_TOPIC:
      const forumTopic = data as IForumTopicDAO;
      if (forumTopic.text) mergeAndDelete(state, EntityTypes.TEXT, forumTopic.text.id, forumPost, 'text');
      if (forumTopic.posts) mergeAndDelete(state, EntityTypes.FORUM_POST, null, forumTopic, 'posts');

      mergeState(state, entityType, forumTopic);
      return;
    case EntityTypes.GROUP:
      const group = data as IGroupDAO;
      if (group.roleConfig) mergeAndDelete(state, EntityTypes.ROLE_CONFIG, group.roleConfig.id, group, 'roleConfig');
      if (group.tags) mergeAndDelete(state, EntityTypes.TAG_JOIN, null, group, 'tags');
      if (group.text) mergeAndDelete(state, EntityTypes.TEXT, group.text.id, group, 'text');
      if (group.groupUsers) mergeAndDelete(state, EntityTypes.GROUP_USER, null, group, 'groupUsers');
      if (group.courseNode) mergeAndDelete(state, EntityTypes.COURSE_NODE, group.courseNode.id, group, 'courseNode');
      mergeState(state, entityType, group);
      return;
    case EntityTypes.GROUP_ACCESS_PERMISSION:
      const groupAccessPermission = data as IGroupAccessPermissionDAO;
      mergeState(state, entityType, groupAccessPermission);
      return;
    case EntityTypes.GROUP_USER:
      const groupUser = data as IGroupUserDAO;
      if (groupUser.organisationUser)
        mergeAndDelete(state, EntityTypes.ORG_USER, groupUser.organisationUser.id, groupUser, 'organisationUser');
      mergeState(state, entityType, groupUser);
      return;
    case EntityTypes.IMAGE:
      const image = data as IImageDAO;
      //if (image.files) mergeAndDelete(state, EntityTypes.IMAGE_FILE, null, image, 'files');

      mergeState(state, entityType, image);
      return;
    case EntityTypes.MODEL:
      const model = data as IModelDAO;
      if (model.entities) mergeAndDelete(state, EntityTypes.MODEL_ENTITY_CONFIG, null, model, 'entities');
      if (model.files) mergeAndDelete(state, EntityTypes.MODEL_FILE, null, model, 'files');
      if (model.position) mergeAndDelete(state, EntityTypes.POSITION, model.position.id, model, 'position');
      if (model.rotation) mergeAndDelete(state, EntityTypes.ROTATION, model.rotation.id, model, 'rotation');

      mergeState(state, entityType, model);
      return;
    case EntityTypes.MODULE:
      const module = data as IModuleDAO;

      const __stage =
        module.stage === ModuleStage.DEEP
          ? ModuleStage.DEEP
          : state[EntityTypes.MODULE][module.id]
          ? state[EntityTypes.MODULE][module.id].__stage === ModuleStage.DEEP
            ? ModuleStage.DEEP
            : ModuleStage.SHALLOW
          : ModuleStage.SHALLOW;

      const __stale = state[EntityTypes.MODULE][module.id]?.__stale
        ? data.loading === LoadingState.LOADED
          ? false
          : true
        : state[EntityTypes.MODULE] &&
          state[EntityTypes.MODULE][module.id] &&
          state[EntityTypes.MODULE][module.id].modified?.date !== module.modified?.date;

      if (module.variable) {
        module.variable.name = module.text?.shortText;
        mergeAndDelete(state, EntityTypes.VARIABLE, module.variable.id, module, 'variable');
      }
      if (module.actions) mergeAndDelete(state, EntityTypes.ACTION, null, module, 'actions');
      if (module.scenes) mergeAndDelete(state, EntityTypes.SCENE, null, module, 'scenes');
      if (module.outs) mergeAndDelete(state, EntityTypes.MODULE_OUT, null, module, 'outs');
      //if (module.tags) mergeAndDelete(state, EntityTypes.TAG_JOIN, null, module, 'tags');
      if (module.image) mergeAndDelete(state, EntityTypes.IMAGE, module.image.id, module, 'image');
      if (module.text) mergeAndDelete(state, EntityTypes.TEXT, module.text.id, module, 'text');
      if (module.clonedVersion)
        mergeAndDelete(state, EntityTypes.MODULE_VERSION, module.clonedVersion.id, module, 'clonedVersion');
      if (module.versions) mergeAndDelete(state, EntityTypes.MODULE_VERSION, null, module, 'versions');
      if (module.courseNode) mergeAndDelete(state, EntityTypes.COURSE_NODE, module.courseNode.id, module, 'courseNode');

      const moduleAssign = data as IModuleAssign;
      if (moduleAssign.sessionAttempt)
        mergeAndDelete(state, EntityTypes.SESSION_ATTEMPT, moduleAssign.sessionAttempt.id, module, 'sessionAttempt');

      mergeState(state, entityType, { ...module, __stage, __stale });
      return;
    case EntityTypes.NOTIFICATION:
      const notification = data as INotificationDAO;

      if (notification.forumMention)
        mergeAndDelete(
          state,
          EntityTypes.FORUM_POST_MENTION,
          notification.forumMention.id,
          notification,
          'forumMention',
        );

      if (notification.accessPermission)
        mergeAndDelete(
          state,
          EntityTypes.COURSE_PROGRESS,
          notification.accessPermission.id,
          notification,
          'accessPermission',
        );

      if (notification.template)
        mergeAndDelete(state, EntityTypes.TEMPLATE, notification.template.id, notification, 'template');

      if (notification.userCertificate)
        mergeAndDelete(
          state,
          EntityTypes.ORG_USER_CERTIFICATE,
          notification.userCertificate.id,
          notification,
          'userCertificate',
        );

      genericMerge(state, notification);
      mergeState(state, entityType, course);
      return;
    case EntityTypes.ORG_CONFIG:
      const orgConfig = data as IOrganisationConfigDAO;

      if (notification.template)
        mergeAndDelete(state, EntityTypes.TEMPLATE, notification.template.id, notification, 'template');

      if (notification.userCertificate)
        mergeAndDelete(
          state,
          EntityTypes.ORG_USER_CERTIFICATE,
          notification.userCertificate.id,
          notification,
          'userCertificate',
        );

      mergeState(state, entityType, orgConfig);
      return;
    case EntityTypes.ORG_USER_COURSE:
      const orgUserCourse = data as IOrgUserCourse;
      //   if (orgUserCourse.coursePermission)
      //   mergeAndDelete(
      //     state,
      //     EntityTypes.COURSE_PERMISSION,
      //     orgUserCourse.coursePermission.id,
      //     orgUserCourse,
      //     'coursePermission',
      //   );

      // if (orgUserCourse.courseProgress)
      //   mergeAndDelete(
      //     state,
      //     EntityTypes.COURSE_PROGRESS,
      //     orgUserCourse.courseProgress.id,
      //     orgUserCourse,
      //     'courseProgress',
      //   );
      mergeState(state, entityType, data);
      return;
    case EntityTypes.ORG_USER:
      const orgUser = data as IOrganisationUserDAO;

      if (orgUser.roleConfig)
        mergeAndDelete(state, EntityTypes.ROLE_CONFIG, orgUser.roleConfig.id, orgUser, 'roleConfig');

      if (orgUser.organisationUserVariables)
        mergeAndDelete(state, EntityTypes.ORG_USER_VARIABLE, null, orgUser, 'organisationUserVariables');

      if (orgUser.variableOverrides)
        mergeAndDelete(state, EntityTypes.ORG_USER_VARIABLE_OVER, null, orgUser, 'variableOverrides');

      if (orgUser.certificates) mergeAndDelete(state, EntityTypes.ORG_USER_CERTIFICATE, null, orgUser, 'certificates');

      if (orgUser.groupUsers) mergeAndDelete(state, EntityTypes.GROUP_USER, null, orgUser, 'groupUsers');

      orgUser.report =
        state[EntityTypes.ORG_USER][orgUser.id] && state[EntityTypes.ORG_USER][orgUser.id].report
          ? true
          : orgUser.organisationUserVariables !== null;

      orgUser.__stale = state[EntityTypes.ORG_USER][orgUser.id]
        ? state[EntityTypes.ORG_USER][orgUser.id].signinId !== orgUser.signinId
        : true;

      mergeState(state, entityType, orgUser);
      return;
    case EntityTypes.ORG_USER_CERTIFICATE:
      const orgUserCert = data as IOrgUserCertificateDAO | IOrgUserCertificateReport;
      // if (orgUserCert.organisationUser)
      //   mergeAndDelete(state, EntityTypes.ORG_USER, orgUserCert.organisationUser.id, orgUserCert, 'organisationUser');

      if (orgUserCert.certificate)
        mergeAndDelete(state, EntityTypes.CERTIFICATE, orgUserCert.certificate.id, orgUserCert, 'certificate');

      mergeState(state, entityType, orgUserCert);
      return;
    case EntityTypes.ORG_USER_VARIABLE:
      const orgUserVar = data as IOrgUserVariableDAO;

      if (orgUserVar.organisationUser)
        mergeAndDelete(state, EntityTypes.ORG_USER, orgUserVar.organisationUser.id, orgUserVar, 'organisationUser');

      if (orgUserVar.variable)
        mergeAndDelete(state, EntityTypes.VARIABLE, orgUserVar.variable.id, orgUserVar, 'variable');

      genericMerge(state, data);

      mergeState(state, entityType, data);
      return;
    case EntityTypes.ORG_USER_VARIABLE_OVER:
      mergeState(state, entityType, data);
      return;
    case EntityTypes.ORG_SYSTEM:
      const orgSystem = data as IOrganisationSystemDAO;
      if (orgSystem.system) mergeAndDelete(state, EntityTypes.SYSTEM, orgSystem.system.id, orgSystem, 'system');
      if (orgSystem.values) mergeAndDelete(state, EntityTypes.ORG_SYSTEM_VALUE, null, orgSystem, 'values');
      if (orgSystem.service)
        mergeAndDelete(state, EntityTypes.SYSTEM_SERVICE, orgSystem.service.id, orgSystem, 'service');
      genericMerge(state, data);
      mergeState(state, entityType, orgSystem);
      return;
    case EntityTypes.ORG_SYSTEM_VALUE:
      const orgSystemValue = data as IOrganisationSystemValue;
      mergeState(state, entityType, orgSystemValue);
      return;
    case EntityTypes.PORTRAIT_VARIANT:
      const portraitVar = data as IPortraitVariantDAO;

      genericMerge(state, portraitVar);
      mergeState(state, entityType, portraitVar);
      return;

    case EntityTypes.SCENE:
      const scene = data as ISceneDAO;
      if (scene.sceneConfig)
        mergeAndDelete(state, EntityTypes.SCENE_CONFIG, scene.sceneConfig.id, scene, 'sceneConfig');
      if (scene.model) mergeAndDelete(state, EntityTypes.MODEL, scene.model.id, scene, 'model');
      if (scene.video) mergeAndDelete(state, EntityTypes.VIDEO, scene.video.id, scene, 'video');
      genericMerge(state, scene);
      mergeState(state, entityType, scene);
      return;

    case EntityTypes.SCENE_CONFIG:
      const sceneConfig = data as ISceneConfigDAO;

      if (data.pImage) mergeAndDelete(state, EntityTypes.IMAGE, data.pImage.id, data, 'pImage');
      if (data.pPosition) mergeAndDelete(state, EntityTypes.POSITION, data.pPosition.id, data, 'pPosition');
      if (data.pRotation) mergeAndDelete(state, EntityTypes.ROTATION, data.pRotation.id, data, 'pRotation');

      genericMerge(state, sceneConfig);
      mergeState(state, entityType, sceneConfig);
      return;

    case EntityTypes.SESSION_ATTEMPT:
      const sessionAttempt = data as ISessionAttempt;
      mergeState(state, entityType, sessionAttempt);
      return;

    case EntityTypes.STRUCTURE:
      const structure = data as IStructureDAO;

      if (structure.groupInstances)
        mergeAndDelete(state, EntityTypes.STRUCTURE_GROUP_INSTANCE, null, structure, 'groupInstances');

      if (structure.outs) mergeAndDelete(state, EntityTypes.STRUCTURE_OUT, null, structure, 'outs');

      mergeState(state, entityType, structure);
      return;
    case EntityTypes.STRUCTURE_GROUP_INSTANCE:
      const structureGroupInstance = data as IStructureGroupInstanceDAO;

      if (structureGroupInstance.group)
        mergeAndDelete(state, EntityTypes.GROUP, structureGroupInstance.group.id, structureGroupInstance, 'group');

      genericMerge(state, structureGroupInstance);
      mergeState(state, entityType, structureGroupInstance);
      return;
    case EntityTypes.SYSTEM:
      const system = data as ISystemDAO;

      if (system.attributes) mergeAndDelete(state, EntityTypes.SYSTEM_ATTRIBUTE, null, system, 'attributes');
      if (system.services) mergeAndDelete(state, EntityTypes.SYSTEM_SERVICE, null, system, 'services');

      mergeState(state, entityType, system);
      return;
    case EntityTypes.SYSTEM_GROUP:
      const systemGroup = data as ISystemGroupDAO;

      if (systemGroup.group) mergeAndDelete(state, EntityTypes.GROUP, systemGroup.group.id, systemGroup, 'group');

      mergeState(state, entityType, systemGroup);
      return;
    case EntityTypes.SYSTEM_ATTRIBUTE:
      const systemAttribute = data as ISystemAttribute;
      mergeState(state, entityType, systemAttribute);
      return;

    case EntityTypes.TEXT:
      const text = data as ITextDAO;
      const searchText = data as ITextSearchDAO;

      if (text.translations) mergeAndDelete(state, EntityTypes.TRANSLATION, null, text, 'translations');
      if (searchText.module) mergeAndDelete(state, EntityTypes.MODULE, searchText.module.id, searchText, 'module');

      mergeState(state, entityType, text);
      return;
    case EntityTypes.TRANSCRIPTION:
      const transcription = data as ITranscriptionDAO;

      if (transcription.items) mergeAndDelete(state, EntityTypes.TRANSCRIPTION_ITEM, null, transcription, 'items');

      mergeState(state, entityType, transcription);
      return;
    case EntityTypes.VERIFICATION:
      const verification = data as IVerificationDAO;
      if (verification.entities) mergeAndDelete(state, EntityTypes.VERIFICATION_ENTITY, null, verification, 'entities');

      genericMerge(state, verification);
      mergeState(state, entityType, verification);
      return;

    case EntityTypes.VARIABLE:
      const variable = data as IVariableDAO;

      if (variable.options) mergeAndDelete(state, EntityTypes.VARIABLE_OPTION, null, variable, 'options');

      genericMerge(state, variable);

      mergeState(state, entityType, variable);
      return;
    case EntityTypes.VIDEO:
      const video = data as IVideoDAO;
      if (video.transcription)
        mergeAndDelete(state, EntityTypes.TRANSCRIPTION, video.transcription.id, video, 'transcription');
      // if (video.files) mergeAndDelete(state, EntityTypes.VIDEO_FILE, null, video, 'files');

      mergeState(state, entityType, video);
      return;
    default:
      mergeState(state, entityType, data);
  }
}

function genericMerge(state, data: any) {
  if (data.text) mergeAndDelete(state, EntityTypes.TEXT, data.text.id, data, 'text');
  if (data.image) mergeAndDelete(state, EntityTypes.IMAGE, data.image.id, data, 'image');
  if (data.node) mergeAndDelete(state, EntityTypes.NODE, data.node.id, data, 'node');
  if (data.position) mergeAndDelete(state, EntityTypes.POSITION, data.position.id, data, 'position');
  if (data.rotation) mergeAndDelete(state, EntityTypes.ROTATION, data.rotation.id, data, 'rotation');
  if (data.tags) mergeAndDelete(state, EntityTypes.TAG_JOIN, null, data, 'tags');
}

function mergeAndDelete(state, type, id, ent, attrib) {
  const clone = cloneDeep(ent[attrib]);
  // dissoc(ent[attrib]);
  merge(state, type, id, clone);
  delete ent[attrib];
}

function mergeState(state, type, entity) {
  const entities = state[type];

  if (!entities) return;
  //changed order of override because bad data coming from server
  entities[entity.id] = entities[entity.id] ? { ...entities[entity.id], ...entity } : entity;
  state[type] = entities;
}

export function mergeCreate(state, action) {
  const { type, parentEntityAttribute, parentEntityId, parentEntityType } = action.meta.arg as ICreate;
  const { id } = action.payload;

  //this is stopping merges across reducers. Not sure why it was there
  // if (!state[type]) return;

  merge(state, type, id, action.payload);
  if (parentEntityAttribute)
    merge(state, parentEntityType, parentEntityId, { id: parentEntityId, [parentEntityAttribute]: id });
}

export function mergeOffline(state, type, id, data) {
  if (!state[type]) return;

  merge(state, type, id, data);
}

export function offlineEntity(state, type, entity) {
  if (!state[type]) return;
  if (!state[type][entity.offlineId] || !entity.id) return;

  const entities = state[type];
  entities[entity.id] = { ...entity, ...entities[entity.offlineId], id: entity.id, offlineId: null };
  delete entities[entity.offlineId];
  state[type] = entities;
}
